// Slot-id generation for the AutoImageEditor.
//
// Each editable image must have a UNIQUE identifier scoped to its exact
// context — page path + DOM ancestry + position. Two images sharing the
// same source URL but rendered in different hotels / rooms / pages / cards
// MUST resolve to different slots so that editing one never leaks into
// another.

// ── Stable, environment-independent slot ids ─────────────────────────
//
// Highest-priority override: `data-cms-slot="<exact.slot.id>"`. When
// present on the image element itself, that string is used as-is — no
// prefix, no scope/role composition. This is the escape hatch for
// components that already subscribe to a specific named slot (e.g.
// PropertyCardGallery → `home.collection.<slug>.image`) and want the
// inline pencil editor to write back to the SAME slot the component
// reads from, so a single edit reflects everywhere that slot is used.
//
// Otherwise, any element (or any of its ancestors) can declare a CMS
// scope via `data-cms-scope="<context>"`. The element itself can
// declare its role via `data-cms-role="<purpose>"`. When BOTH are
// present (or the image lives inside a scoped ancestor), the slot id
// becomes:
//
//     cms.<scope>.<role>
//
// Examples:
//   <article data-cms-scope="hotel.kasbah-hotel-tombouctou">
//     <img data-cms-role="card" />          → cms.hotel.kasbah-hotel-tombouctou.card
//     <img data-cms-role="thumb.0" />       → cms.hotel.kasbah-hotel-tombouctou.thumb.0
//   </article>
//
// This id is stable across environments: it never depends on the
// image URL, DOM position, or page path. Editing in Production and
// running Mirror DB always reaches Preview correctly.
//
// If neither `data-cms-slot` nor `data-cms-scope` is declared, we
// fall back to the legacy URL+DOM+path fingerprint so existing
// images and slots keep working.
const STABLE_SLOT_ATTR = "data-cms-slot";
const STABLE_SCOPE_ATTR = "data-cms-scope";
const STABLE_ROLE_ATTR = "data-cms-role";

const SLOT_ID_SAFE = /[^a-z0-9._-]/gi;

// Sanitises a `data-cms-scope` or `data-cms-role` value into a slot-id
// fragment. Trims to 240 chars to leave headroom for long editorial
// scopes (e.g. hotel-video-heritage grids where the scope naturally
// runs 80-100+ chars once slug + section + subsection compose). At 80
// we were seeing REAL collisions in the wild — the entire "Property
// Films" secondary strip on hotels with long slugs collapsed to a
// single slot id, so only the featured tile got a pencil in Edit Mode.
// 240 accommodates every scope currently used on the site with plenty
// of runway for future nesting, without bloating persisted slot keys.
const sanitiseScopeOrRole = (raw) => {
  if (!raw) return "";
  return String(raw).replace(SLOT_ID_SAFE, "-").toLowerCase().slice(0, 240);
};

// `data-cms-slot` accepts a fully-qualified slot id — sanitise it
// but preserve dots (e.g. "home.collection.kasbah.image"). Limit at
// 120 chars defensively.
const sanitiseExplicitSlot = (raw) => {
  if (!raw) return "";
  return String(raw).replace(SLOT_ID_SAFE, "-").toLowerCase().slice(0, 120);
};

const findScopeAncestor = (el) => {
  let node = el;
  let depth = 0;
  while (node && node !== document.body && depth < 15) {
    if (node.getAttribute && node.getAttribute(STABLE_SCOPE_ATTR)) return node;
    node = node.parentElement;
    depth += 1;
  }
  return null;
};

/**
 * Returns a stable slot id when the element (or any ancestor) opts in.
 *
 *  1. Explicit `data-cms-slot="<id>"` on the element wins — used verbatim.
 *  2. Otherwise an ancestor `data-cms-scope` combines with the element's
 *     own `data-cms-role` (defaults to "image") into `cms.<scope>.<role>`.
 *  3. Returns null when no stable identifier is declared.
 */
export const stableSlotFromElement = (el) => {
  if (!el) return null;
  const explicit =
    el.getAttribute && el.getAttribute(STABLE_SLOT_ATTR);
  if (explicit) {
    const cleaned = sanitiseExplicitSlot(explicit);
    return cleaned || null;
  }
  const scopeNode = findScopeAncestor(el);
  if (!scopeNode) return null;
  const scope = sanitiseScopeOrRole(scopeNode.getAttribute(STABLE_SCOPE_ATTR));
  if (!scope) return null;
  const role =
    sanitiseScopeOrRole(el.getAttribute && el.getAttribute(STABLE_ROLE_ATTR)) ||
    "image";
  return `cms.${scope}.${role}`;
};

// ── Legacy URL+DOM+path slot id (kept for backward compatibility) ────

const djb2 = (str) => {
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = ((h * 33) + str.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
};

const NOISY_PARAMS = ["t", "ts", "v", "ver", "rand", "_", "cb"];

const normaliseSrc = (src) => {
  if (!src || typeof src !== "string") return "";
  try {
    const base = typeof window !== "undefined" ? window.location.origin : "https://x.local";
    const u = new URL(src, base);
    const params = new URLSearchParams(u.search);
    NOISY_PARAMS.forEach((k) => params.delete(k));
    const cleanQuery = params.toString();
    return `${u.hostname}${u.pathname}${cleanQuery ? "?" + cleanQuery : ""}`.toLowerCase();
  } catch {
    return src.toLowerCase();
  }
};

// Sanitise a value for use as a readable slot tag.
const sanitiseTag = (raw, max = 32) => {
  if (!raw) return "";
  return String(raw).slice(-max).replace(/[^a-z0-9_-]/gi, "-").toLowerCase();
};

// Slugify the current pathname to a readable token (no slashes).
//   "/hotels/kasbah-hotel-xaluca-arfoud" → "_hotels_kasbah-hotel-xaluca-arfoud"
const pathToken = () => {
  if (typeof window === "undefined") return "_";
  const p = window.location.pathname || "/";
  const clean = p.replace(/[^a-z0-9/_-]/gi, "").replace(/\/+/g, "_").replace(/^_+|_+$/g, "");
  return (clean || "root").slice(0, 80).toLowerCase();
};

// Build a stable, context-aware path signature for an element. Walks up to
// `MAX_DEPTH` ancestors, preferring stable identifiers (data-testid,
// data-cms-scope) and falling back to `tag:nth-of-type` at each level.
const MAX_DEPTH = 10;
const buildDomPath = (el) => {
  const segments = [];
  let node = el;
  let depth = 0;
  while (node && node !== document.body && depth < MAX_DEPTH) {
    const parent = node.parentElement;
    if (!parent) break;
    const testId = node.getAttribute && node.getAttribute("data-testid");
    const cmsScope = node.getAttribute && node.getAttribute("data-cms-scope");
    if (cmsScope) {
      segments.unshift(`s:${cmsScope}`);
    } else if (testId) {
      segments.unshift(`t:${testId}`);
    } else {
      const tag = node.tagName ? node.tagName.toLowerCase() : "x";
      // index among same-tag siblings (stable across re-renders)
      let idx = 0;
      let pos = 0;
      for (const sib of parent.children) {
        if (sib.tagName === node.tagName) {
          if (sib === node) idx = pos;
          pos += 1;
        }
      }
      segments.unshift(`${tag}:${idx}`);
    }
    node = parent;
    depth += 1;
  }
  return segments.join("/") || "root";
};

// PUBLIC ─ used by AutoImageEditor for <img> elements.
//
// Returns a slot id that is unique per *position* of the image in the
// DOM on the current page. The slot id is **URL-independent**: it
// depends only on the current pathname and the structural DOM path
// of the image — NEVER on the image's `src`. This guarantees that
// the same image position generates the same slot id in Preview and
// in Production even when the baseline URL differs between
// environments, so "Mirror from Production" propagates every edit
// without exception.
//
// Priority chain:
//   1. `data-cms-slot="<exact.id>"` on the element (verbatim).
//   2. ancestor `data-cms-scope` + element `data-cms-role`
//      composing `cms.<scope>.<role>`.
//   3. legacy URL-independent fallback: `auto.<path>.<tag>.<djb2(dom)>`.
export const slotFromImageElement = (img) => {
  if (!img) return null;
  // Prefer the stable, environment-independent slot id when a
  // `data-cms-scope` is declared on the image or any ancestor.
  const stable = stableSlotFromElement(img);
  if (stable) return stable;
  const tag = sanitiseTag(img.tagName ? img.tagName.toLowerCase() : "img", 8) || "img";
  const path = pathToken();
  const dom = buildDomPath(img);
  return `auto.${path}.${tag}.${djb2(dom)}`;
};

// PUBLIC ─ returns the LEGACY (URL-fingerprinted) slot id that earlier
// versions of the CMS persisted. Used as a non-destructive lookup
// fallback in `applyOverrides`: when the new stable id has no cached
// override, the cache is consulted again with the legacy id so that
// pre-refactor edits saved in Production continue to apply after
// "Mirror from Production" — without requiring a destructive
// migration of the `image_slots` collection.
//
// The legacy format was:
//
//     auto.<path>.<urlFilenameTag>.<djb2(domPath)>.<djb2(url).slice(0,4)>
//
// Returns `null` when the element opts into the stable scheme via
// `data-cms-scope` / `data-cms-slot` — those IDs never had a legacy
// equivalent so there's nothing to look up.
export const legacySlotFromImageElement = (img) => {
  if (!img) return null;
  // Scoped images never had a legacy id — skip the fallback for them.
  if (stableSlotFromElement(img)) return null;
  const original = (img.dataset && img.dataset.cmsSrc) || img.getAttribute("src") || img.src;
  const norm = normaliseSrc(original);
  const fileMatch = norm.match(/\/([^/?#]+?)(?:\.[a-z0-9]+)?(?:\?|$)/i);
  const tag = sanitiseTag(fileMatch ? fileMatch[1] : "image", 24) || "image";
  const path = pathToken();
  const dom = buildDomPath(img);
  return `auto.${path}.${tag}.${djb2(dom)}.${djb2(norm).slice(0, 4)}`;
};

// PUBLIC ─ used by AutoImageEditor for inline background-image elements.
//
// Same env-independent guarantee as `slotFromImageElement`: the slot
// id depends only on the page path and the structural DOM path of
// the background-bearing element, not on the CSS `background-image`
// URL.
export const slotFromBgElement = (el /* bgUrl unused; kept for back-compat */) => {
  if (!el) return null;
  // Same stable-id preference as <img>: if a CMS scope is declared
  // anywhere up the tree, use that — otherwise fall back to a URL-
  // independent path+DOM fingerprint.
  const stable = stableSlotFromElement(el);
  if (stable) return stable.replace(/^cms\./, "cms.bg-");
  const tag = sanitiseTag(el.tagName ? el.tagName.toLowerCase() : "bg", 8) || "bg";
  const path = pathToken();
  const dom = buildDomPath(el);
  return `auto.${path}.bg-${tag}.${djb2(dom)}`;
};

// PUBLIC ─ legacy URL-fingerprinted background-element slot id. Used as a
// non-destructive lookup fallback. Returns null for scoped elements.
export const legacySlotFromBgElement = (el, bgUrl) => {
  if (!el) return null;
  if (stableSlotFromElement(el)) return null;
  const norm = normaliseSrc(bgUrl);
  const fileMatch = norm.match(/\/([^/?#]+?)(?:\.[a-z0-9]+)?(?:\?|$)/i);
  const tag = sanitiseTag(fileMatch ? fileMatch[1] : "bg", 24) || "bg";
  const path = pathToken();
  const dom = buildDomPath(el);
  return `auto.${path}.bg-${tag}.${djb2(dom)}.${djb2(norm).slice(0, 4)}`;
};

// Legacy URL-only slot (kept for the explicit <EditableImage> contract,
// where the caller supplies an explicit `slot` prop — that path is
// unchanged. Also still exported so any historical callers don't break.)
export const slotFromSrc = (src) => {
  const norm = normaliseSrc(src);
  if (!norm) return null;
  const fileMatch = norm.match(/\/([^/?#]+?)(?:\.[a-z0-9]+)?(?:\?|$)/i);
  let tag = fileMatch ? fileMatch[1] : "image";
  tag = sanitiseTag(tag, 40);
  if (!tag) tag = "image";
  return `auto.${tag}.${djb2(norm)}`;
};

// PUBLIC ─ used by AutoTextEditor for text-bearing leaf elements.
// Returns a stable slot id derived solely from the element's POSITION
// in the DOM so the same heading/paragraph/button-text always resolves
// to the SAME slot — both across page reloads AND across language
// switches (the text content itself is NOT part of the id).
export const slotFromTextElement = (el) => {
  if (!el) return null;
  const tag = sanitiseTag(el.tagName ? el.tagName.toLowerCase() : "text", 8) || "text";
  const path = pathToken();
  const dom = buildDomPath(el);
  return `text.${path}.${tag}.${djb2(dom)}`;
};

// PUBLIC ─ used by AutoTextEditor.applyPlaceholders to override the
// `placeholder` attribute of form inputs from persisted CMS data.
// Shares the same DOM-position algorithm as `slotFromTextElement` so
// the crawler (`/app/scripts/bootstrap_text_slots.py`) and the runtime
// resolve to the exact same slot id. The `.ph.` marker keeps the
// namespace distinct from the input's own visible text (labels are
// separate elements).
export const slotFromPlaceholderElement = (el) => {
  if (!el) return null;
  const path = pathToken();
  const dom = buildDomPath(el);
  return `text.${path}.ph.${djb2(dom)}`;
};

// Extract a URL from a CSS background-image value ('url("...")' or 'url(...)')
export const extractBgUrl = (bgImg) => {
  if (!bgImg || bgImg === "none") return null;
  const m = bgImg.match(/url\((['"]?)([^'")]+)\1\)/);
  return m ? m[2] : null;
};
